<!--
  VariantCard — Phase 1-A (variant cards wired to real Artwork).

  Card: "Phase F - Artwork Repository" -- https://trello.com/c/hYlwyKYT
  Pebble: Phase 1-A: Variant cards wired to real Artwork.

  Plan: planning/design-handoffs/handoff_2026-06-13/implementation-plan/
        02-product-editor.md (Day 4 — grouped variant cards).

  Renders one card per variant row on the parent placement. The parent
  (DecorationInspector) computes the iteration order AND resolves the
  matching Artwork record via the artworks Pinia store; this component
  is the per-card surface.

  Per the design:
    Header row: "Light-garment artwork" | "Now previewing" | "N colors"
    Body:      thumb | name + filename + ink + version + readability | Replace

  PR-N4: the legacy variant_key (A/B/S1) column is dropped. Headers
  read variant.kind directly; data-testids include the variant id so
  tests have a stable selector even though the user never sees the
  per-row identifier.

  Phase 1-A real-data paths (when `artwork` prop is populated):
    - filename:        artwork.filename
    - ink hex / label: artwork.ink_color_hex via inkColorName lookup
    - version:         "v" + artwork.version
    - lifecycle chip:  driven by `lifecycle` prop (DecorationInspector
                       computed via productLifecycleForVariant — F-4)

  Fallback paths (when `artwork` is null — pre-Phase-F products with no
  matching Artwork row, or in-flight artworks-store load):
    - filename:        variant.artwork_filename when present, else the
                       last 8 chars of variant.artwork_file_id with NO
                       extension (that id is an opaque identifier, not a
                       filename -- it used to get a hardcoded ".emb",
                       which mislabelled every non-embroidery decoration)
    - ink hex / label: variant.ink_color_hex via legacy 2-entry map
    - version:         hardcoded "v1"

  Readability is a REAL WCAG ink-vs-garment contrast check (#2791),
  computed in utils/artworkReadability and shared with RoutingStrip. It
  reports the worst color in the variant's routed set and renders
  NOTHING when the ink color was never declared -- it no longer shows a
  hardcoded "Readable" for every artwork.
-->
<template>
    <article
        :data-testid="`variant-card-${testidSuffix}`"
        :data-variant-id="variantId"
        :data-previewing="isPreviewing ? 'true' : 'false'"
        class="variant-card"
        :class="[kindBgClass, { 'is-previewing': isPreviewing }]"
    >
        <!--
          Inline delete-confirm overlay. Per the 2026-06-13 product-editor
          handoff (editor-a-app.js variantConfirmCard), clicking the trash
          icon in the card header replaces the card body with a confirm
          prompt that names the variant + the routing fallback consequence.
          Cancel restores the card; Confirm emits `delete` and the parent
          (DecorationInspector) calls the productEditor store action.
        -->
        <div
            v-if="confirmingDelete"
            data-testid="variant-card-delete-confirm"
            class="variant-card-delete-confirm"
        >
            <div class="variant-card-delete-confirm-header">
                <span class="material-icons" aria-hidden="true">delete_outline</span>
                <span>Delete this artwork?</span>
            </div>
            <!--
              Shade-artwork UX polish: when deleting would leave colors
              with NO artwork on this placement (strandedColorCount
              from DecorationInspector), name the gap concretely.
              Otherwise keep the lighter fallback copy.
            -->
            <p class="variant-card-delete-confirm-body">
                {{ deleteConfirmBody }}
            </p>
            <div class="variant-card-delete-confirm-actions">
                <button
                    type="button"
                    data-testid="variant-card-delete-cancel"
                    class="variant-card-delete-cancel"
                    @click="onCancelDelete"
                >
                    Cancel
                </button>
                <button
                    type="button"
                    data-testid="variant-card-delete-confirm-button"
                    class="variant-card-delete-confirm-button"
                    @click="onConfirmDelete"
                >
                    <span class="material-icons" aria-hidden="true">delete</span>
                    Delete artwork
                </button>
            </div>
        </div>
        <template v-else>
        <header
            :data-testid="`variant-card-${testidSuffix}-header`"
            class="variant-card-header"
        >
            <!--
              Slice 1 (editable variant cards): click-to-edit custom name.
              When not editing, the title is a button that opens an inline
              v-text-field on click (Enter/blur commit, Escape cancel,
              empty commit clears the name back to the kind label). When a
              custom name IS set, a small role chip sits beside it so the
              routing role stays visible even though the header shows the
              custom name.
            -->
            <span
                v-if="!editingName"
                class="variant-card-title-wrap"
            >
                <button
                    type="button"
                    :data-testid="`variant-card-${testidSuffix}-title-edit`"
                    class="variant-card-title variant-card-title-btn"
                    :title="`Rename ${headerText}`"
                    @click="onStartEditName"
                >
                    {{ headerText }}
                </button>
                <span
                    v-if="hasCustomName"
                    :data-testid="`variant-card-${testidSuffix}-role-chip`"
                    class="variant-card-role-chip"
                >
                    {{ roleChipLabel }}
                </span>
            </span>
            <v-text-field
                v-else
                ref="nameInputRef"
                v-model="nameDraft"
                :data-testid="`variant-card-${testidSuffix}-name-input`"
                class="variant-card-name-input"
                density="compact"
                variant="outlined"
                hide-details
                autofocus
                :maxlength="80"
                @keydown.enter.prevent="onCommitName"
                @keydown.esc.prevent="onCancelEditName"
                @blur="onCommitName"
            />
            <span
                v-if="isPreviewing"
                :data-testid="`variant-card-${testidSuffix}-now-previewing`"
                class="variant-card-now-previewing"
            >
                <span class="variant-card-now-dot" aria-hidden="true"></span>
                Now previewing
            </span>
            <!--
              The "N colors" chip is always a STATIC count. MVP
              colorization is light/dark only, so a card's colors are
              always DERIVED (render-time tally) -- there is no
              per-SKU set for the user to toggle (design 3.4).
            -->
            <span
                :data-testid="`variant-card-${testidSuffix}-color-count`"
                class="variant-card-color-count"
            >
                {{ colorCountLabel }}
            </span>
            <!--
              Slice 2 (editable variant cards): kind menu. A kebab that
              opens a Vuetify v-menu (Slice 0 pattern) with exactly three
              routing-role options -- All light / All dark / Custom
              colors. The already-owned catch-all option is disabled with
              a hint. Presentational combined-shade cards hide it (they do
              not own a single mutable variant).
            -->
            <v-menu
                v-if="showKindMenu"
                v-model="kindMenuOpen"
                location="bottom end"
                :close-on-content-click="false"
            >
                <template #activator="{ props: activatorProps }">
                    <button
                        type="button"
                        v-bind="activatorProps"
                        :data-testid="`variant-card-${testidSuffix}-kind-menu-trigger`"
                        class="variant-card-kind-trigger"
                        :aria-label="`Change routing for ${headerText}`"
                        title="Change what colors this artwork routes to"
                        @click="onOpenKindMenu"
                    >
                        <span class="material-icons" aria-hidden="true">tune</span>
                    </button>
                </template>
                <div
                    :data-testid="`variant-card-${testidSuffix}-kind-menu`"
                    class="variant-card-kind-menu"
                    role="menu"
                    aria-label="Routing kind"
                >
                    <button
                        v-for="item in kindMenuItems"
                        :key="item.kind"
                        type="button"
                        role="menuitemradio"
                        :aria-checked="item.isCurrent ? 'true' : 'false'"
                        :data-testid="`variant-card-${testidSuffix}-kind-option-${item.kind}`"
                        class="variant-card-kind-option"
                        :class="{ 'is-current': item.isCurrent }"
                        :disabled="item.disabled"
                        :title="item.disabled ? item.hint : ''"
                        @click="onPickKind(item)"
                    >
                        <span class="variant-card-kind-option-label">
                            {{ item.label }}
                        </span>
                        <span
                            v-if="item.isCurrent"
                            class="variant-card-kind-option-check material-icons"
                            aria-hidden="true"
                        >check</span>
                        <span
                            v-else-if="item.disabled"
                            class="variant-card-kind-option-hint"
                        >{{ item.hint }}</span>
                    </button>
                </div>
            </v-menu>
            <!--
              Trash icon — matches the design handoff's vc-del button
              (editor-a-app.js variantCard, title "Delete Variant ...").
              Renders to the right of the color-count chip so it tracks
              the header row's wrap behavior.
            -->
            <button
                type="button"
                :data-testid="`variant-card-${testidSuffix}-delete`"
                class="variant-card-delete-trigger"
                :aria-label="`Delete ${headerText}`"
                title="Delete artwork"
                @click="onRequestDelete"
            >
                <span class="material-icons" aria-hidden="true">delete_outline</span>
            </button>
        </header>
        <!--
          PR-L3 — kind-aware "Routes: ..." subline.
          Renders ONLY when the variant carries a recognized `kind` (the
          new variant model fields). Legacy variants without `kind` get
          no subline; they still rely on the header / color-count chip
          for routing context. Production data comes through PR-J's
          serializer which always sets kind, so this v-if guard is
          purely a defensive carve-out for fixtures/tests that omit it.
        -->
        <p
            v-if="routesSubline"
            :data-testid="`variant-card-${testidSuffix}-routes-sub`"
            :title="routesSublineTitle"
            class="variant-card-routes-sub"
        >
            {{ routesSubline }}
        </p>

        <!--
          EMPTY SHADE (Jake, 2026-10-21): orders skip this route, so garments
          of this shade are produced with NO decoration on this placement.
          Prominent on purpose, and it says how to fix it.
        -->
        <div
            v-if="emptyShade"
            :data-testid="`variant-card-${testidSuffix}-empty-shade-warning`"
            class="variant-card-empty-shade"
            role="alert"
        >
            <span class="material-icons" aria-hidden="true">warning</span>
            <div>
                <strong>No decoration on {{ emptyShadeName }} garments.</strong>
                {{ emptyShadeNameCap }} garments of this product will be
                produced without a decoration here. To fix it, use
                <strong>Replace</strong> on this card to add one<template
                    v-if="variant.kind === 'catch_all_dark'"
                >, or choose <strong>Use for all colors</strong> on the
                light-garment card to put that decoration on every color</template><template
                    v-else
                > (pick the same decoration to put it on every color)</template>.
            </div>
        </div>

        <div class="variant-card-body">
            <!--
              The picture is the BOUND COLORWAY when there is one (Jake,
              2026-10-22), else the version's default preview. The default
              stays up while the colorway render loads and on every failure,
              so the card never shows a broken image. `data-thumb-source` and
              `data-colorway-thumb` say which picture is up, for tests and
              ui-runner scenarios.
            -->
            <div
                :data-testid="`variant-card-${testidSuffix}-thumb`"
                :data-thumb-source="colorwayThumbUrl ? 'colorway' : 'default'"
                :data-colorway-thumb="colorwayThumbState"
                class="variant-card-thumb"
                :class="{ 'is-loading-colorway': colorwayThumbState === 'loading' }"
            >
                <DecorationArtThumb
                    v-if="variant.artwork_file_id && thumbnailUrl"
                    :src="thumbnailUrl"
                    :ink-hex="colorwayThumbUrl ? null : (artwork?.dominant_ink_hex ?? null)"
                    :ink-mixed="!colorwayThumbUrl && artwork?.dominant_ink_mixed === true"
                    :alt="`${variantAltLabel} artwork`"
                    data-testid="variant-card-thumb-image"
                    @error="onThumbError"
                />
                <div
                    v-else
                    data-testid="variant-card-thumb-empty"
                    class="variant-card-thumb-empty"
                >
                    No artwork
                </div>
                <!--
                  Both spans sit AFTER the v-if/v-else pair on purpose: a
                  v-if between them would capture the v-else and render the
                  empty tile beside a real picture.

                  The first tells a screen reader the picture is the chosen
                  threads (the swatches say which), and gives a scenario an
                  element to wait for: it mounts only once the colorway
                  picture is the one up.
                -->
                <span
                    v-if="colorwayThumbUrl && variant.artwork_file_id"
                    :data-testid="`variant-card-${testidSuffix}-thumb-colorway`"
                    class="variant-card-sr-only"
                >Shown in the chosen thread colors</span>
                <span
                    v-if="colorwayThumbState === 'loading' && variant.artwork_file_id && thumbnailUrl"
                    :data-testid="`variant-card-${testidSuffix}-thumb-loading`"
                    class="variant-card-thumb-loading"
                    role="status"
                    aria-label="Rendering thread colors"
                ></span>
            </div>

            <div class="variant-card-meta">
                <!--
                  Jake, 2026-10-21: "I should be able to click the name of the
                  decoration to go to it in the decoration library." A button
                  only when there IS a library artwork to open; the inspector
                  does the navigation (it owns the router and the way back).
                -->
                <button
                    v-if="libraryArtworkId != null"
                    type="button"
                    :data-testid="`variant-card-${testidSuffix}-artwork-name`"
                    class="variant-card-art-name variant-card-art-link"
                    :title="`Open ${artworkName} in the Decoration Library`"
                    @click="onOpenArtwork"
                >
                    {{ artworkName }}
                </button>
                <div
                    v-else
                    :data-testid="`variant-card-${testidSuffix}-artwork-name`"
                    class="variant-card-art-name"
                >
                    {{ artworkName }}
                </div>
                <div
                    :data-testid="`variant-card-${testidSuffix}-filename`"
                    class="variant-card-filename"
                >
                    {{ filename }}
                </div>
                <div class="variant-card-meta-row">
                    <span
                        :data-testid="`variant-card-${testidSuffix}-ink`"
                        class="variant-card-ink"
                    >
                        <span
                            class="variant-card-ink-dot"
                            :style="inkDotStyle"
                            aria-hidden="true"
                        ></span>
                        Ink &middot; {{ inkLabel }}
                    </span>
                    <span
                        :data-testid="`variant-card-${testidSuffix}-version`"
                        class="variant-card-version"
                    >{{ versionLabel }}</span>
                    <span
                        data-testid="variant-card-lifecycle-chip"
                        class="variant-card-chip"
                        :data-state="lifecycleState"
                    >{{ lifecycleLabel }}</span>
                </div>
                <div class="variant-card-meta-row">
                    <!--
                      #2791: rendered ONLY when a real verdict exists. A
                      null verdict means the ink color was never declared
                      -- we show no badge rather than an unearned
                      "Readable". The garment name is the color actually
                      judged (the worst in the routed set); it used to be
                      the literal string "White" regardless of garment.
                    -->
                    <span
                        v-if="readability"
                        :data-testid="`variant-card-${testidSuffix}-readability`"
                        class="variant-card-readability"
                        :data-state="readability.ok ? 'ok' : 'warn'"
                    >
                        <span class="material-icons variant-card-mi" aria-hidden="true">
                            {{ readability.ok ? "check_circle" : "warning" }}
                        </span>
                        {{ readability.label
                        }}<template v-if="readability.worstColorName">
                            on {{ readability.worstColorName }}</template
                        >
                        &middot; {{ readability.ratio.toFixed(1) }}:1
                    </span>
                </div>
            </div>
        </div>

        <!--
          Colorway binding (Card: Embroidery colorway editing MUST-SHIP,
          https://trello.com/c/2hcgDM7J).

          THE SEPARATION THIS IMPLEMENTS (Jake, 2026-08-25). A decoration is
          an asset reused across many products in many stores, so it CANNOT
          know product colours -- the deco library therefore declares only an
          abstract `substrate_target` of light/dark/undeclared. THIS surface
          knows the real garment colours, so it resolves that declaration
          against them. The resolution produces a DEFAULT (Jake's word for it
          is "correct(ish)") and never a rule: every option below stays
          enabled whatever the variant's kind, because a disabled option is a
          constraint however it is labelled, and it would hand the deco
          library authority over a choice that belongs to the distributor
          looking at the actual colours.

          The whole control is v-if'd away unless this decoration is
          EMBROIDERY (file format .dst/.emb) and its artwork version has
          saved colorways. Non-embroidery has no picker at all: a colorway
          is a thread mapping, a raster or vector has no stops to map, and
          an empty picker would assert the concept applies and is merely
          unfilled. See showColorwayControl for why the guard reads the
          format rather than colorways.length.
        -->
        <div
            v-if="showColorwayControl"
            :data-testid="`variant-card-${testidSuffix}-colorway`"
            class="variant-card-colorway"
        >
            <v-menu
                v-model="colorwayMenuOpen"
                location="bottom start"
                :close-on-content-click="false"
            >
                <template #activator="{ props: activatorProps }">
                    <button
                        type="button"
                        v-bind="activatorProps"
                        :data-testid="`variant-card-${testidSuffix}-colorway-trigger`"
                        class="variant-card-colorway-trigger"
                        :aria-label="`Choose thread colors for ${headerText}`"
                        title="Choose which saved colorway this artwork sews"
                        @click="colorwayMenuOpen = true"
                    >
                        <span class="material-icons" aria-hidden="true">palette</span>
                        {{ colorwayTriggerLabel }}
                    </button>
                </template>
                <div
                    :data-testid="`variant-card-${testidSuffix}-colorway-menu`"
                    class="variant-card-colorway-menu"
                    role="menu"
                    aria-label="Colorway"
                >
                    <button
                        v-for="opt in colorwayOptions"
                        :key="opt.id"
                        type="button"
                        role="menuitemradio"
                        :aria-checked="opt.isSelected ? 'true' : 'false'"
                        :data-testid="`variant-card-${testidSuffix}-colorway-option-${opt.id}`"
                        class="variant-card-colorway-option"
                        :class="{ 'is-current': opt.isSelected }"
                        @click="onPickColorway(opt)"
                    >
                        <span class="variant-card-colorway-option-label">
                            {{ opt.name }}
                        </span>
                        <!--
                          The suggestion marker. v-if on the MODEL rather than
                          a CSS class so a test (and a screenshot) can tell a
                          suggested option from an ordinary one -- mirrors how
                          ColorwayEditor marks the chosen substrate.

                          It is a HINT, never a gate: the option stays
                          clickable and every other option stays clickable too.
                        -->
                        <span
                            v-if="opt.isDefault && !opt.isSelected"
                            :data-testid="`variant-card-${testidSuffix}-colorway-default-${opt.id}`"
                            class="variant-card-colorway-suggested"
                        >Suggested</span>
                        <span
                            v-if="opt.isSelected"
                            class="variant-card-colorway-check material-icons"
                            aria-hidden="true"
                        >check</span>
                    </button>
                    <!--
                      Create-new. An ACTION, not a member of the list above:
                      role="menuitem" rather than "menuitemradio", and no
                      aria-checked, because the variant can never be bound to
                      "create a new colorway". Giving it the radio role would
                      make a screen reader announce it as a fourth pickable
                      colorway.

                      It opens the decoration library's ColorwayEditor rather
                      than authoring inline (design-philosophy principle 2).
                      Authoring means assigning a thread to every stop in the
                      stitch file; that mechanism already exists, and a second
                      one on this card would drift from it.
                    -->
                    <button
                        type="button"
                        role="menuitem"
                        :data-testid="`variant-card-${testidSuffix}-colorway-create`"
                        class="variant-card-colorway-create"
                        @click="onCreateColorway"
                    >
                        <span class="material-icons" aria-hidden="true">add</span>
                        Create a new colorway
                    </button>
                </div>
            </v-menu>

            <!--
              The bound SNAPSHOT (ADR 0044). Rendered from
              `variant.colorway_threads_json` -- the copy this variant
              actually sews -- and NEVER by reaching through `colorway_id` to
              the live colorway row, which may since have been edited.

              DELIBERATELY MAKES NO FRESHNESS CLAIM. Nothing in the system
              marks this snapshot stale when its source changes; that is the
              intended behaviour, not a gap. So there is no "in sync" / "up to
              date" wording and no refresh affordance here -- either would be
              a promise the system does not keep. `_serialize_variant` refuses
              to emit an `is_current` flag for the same reason.
            -->
            <div
                v-if="boundColorway"
                :data-testid="`variant-card-${testidSuffix}-colorway-summary`"
                class="variant-card-colorway-summary"
            >
                <span
                    v-for="(thread, i) in boundColorway.threads"
                    :key="i"
                    :data-testid="`variant-card-${testidSuffix}-colorway-swatch-${i}`"
                    class="variant-card-colorway-swatch"
                    :style="{ backgroundColor: thread.hex || 'transparent' }"
                    :title="colorwayThreadTitle(thread)"
                    aria-hidden="true"
                ></span>
                <span
                    :data-testid="`variant-card-${testidSuffix}-colorway-thread-count`"
                    class="variant-card-colorway-count"
                >{{ boundColorway.threadCountLabel }}</span>
            </div>
            <!--
              #687: an unbound combined card says so. Nothing is chosen, so
              the preview sews the stitch file's own embedded colors.
            -->
            <p
                v-if="showUnboundColorwayPrompt"
                :data-testid="`variant-card-${testidSuffix}-colorway-unbound-prompt`"
                class="variant-card-colorway-unbound-prompt"
            >No colorway chosen: preview shows the file's own thread colors. Pick one or Split light / dark</p>
        </div>

        <!--
          Action buttons live in their OWN full-width row BELOW the
          thumb+meta body (Jake 2026-07-10). Previously nested as a
          squeezed side-column inside .variant-card-body, the long
          "Send for approval" label (white-space:nowrap, can't shrink)
          overflowed the narrow right-rail card and collided with
          Replace. As a full-width sibling row the buttons share the card
          width and never overflow.

          UPDATED 2026-09-02: that row now WRAPS instead of shrinking.
          Sharing the width via flex:1 1 0 + min-width:0 solved overflow by
          letting each button render narrower than its own label, and at
          three buttons in a 340px rail it truncated them to "Use for a..."
          / "Send re...". See the .variant-card-actions rule for the width
          arithmetic. The labels below are unchanged -- none of them was
          ever the problem.
        -->
        <div
            class="variant-card-actions"
            :data-testid="`variant-card-${testidSuffix}-actions`"
        >
            <button
                type="button"
                :data-testid="`variant-card-${testidSuffix}-replace`"
                class="variant-card-replace"
                @click="onReplace"
            >
                Replace
            </button>
            <!--
              Split / merge routes (card https://trello.com/c/vc8U5340).

              Exactly one of these ever renders, because they are the two
              directions of one reversible move and the row is only ever on
              one side of it. Split shows on the all-colors row; merge shows
              on the light row of a pair. Neither shows on a dark row -- the
              dark row is what gets created and destroyed, so acting on it
              would name the object rather than the intent.

              Deliberately here in the actions row rather than in the kind
              menu: this is not a re-labelling of one route, it CREATES or
              DESTROYS a route, and burying a create/destroy inside a menu of
              radio options would present it as the same kind of choice.
            -->
            <button
                v-if="standsForAllColors"
                type="button"
                :data-testid="`variant-card-${testidSuffix}-split-shades`"
                class="variant-card-split"
                title="Add a separate artwork for dark garment colors"
                @click="onSplitShades"
            >
                Split light / dark
            </button>
            <button
                v-else-if="canMergeShades"
                type="button"
                :data-testid="`variant-card-${testidSuffix}-merge-shades`"
                class="variant-card-merge"
                title="Use this one artwork for every garment color"
                @click="onMergeShades"
            >
                Use for all colors
            </button>
            <!--
              Task #70 (Path B glue): "Send for approval" button next
              to Replace. Only renders when the variant has an
              artwork_file_id (= preview is rendered) so the modal
              always opens with a valid decoration_artifacts row id.
              Emits 'send-for-approval' so the parent
              (DecorationInspector) can host the existing
              SendForApprovalModal — wired in PR #1334. We do NOT
              mount the modal here to keep VariantCard dumb.
            -->
            <!--
              Issue #2179 Problem 1: the label reflects the artwork
              lifecycle. Once the artwork has already been sent for
              approval (awaiting_approval / in_review) the button becomes
              "Send reminder" -- clicking it re-opens the SAME send modal
              and emits the SAME 'send-for-approval' event (no new
              endpoint; the send endpoint already returns per-client
              ``already_sent`` so a re-send is a reminder). Otherwise it
              stays "Send for approval". A native ``title`` carries the
              reminder tooltip (matches the icon-button tooltip idiom
              used elsewhere in this card).
            -->
            <button
                v-if="canSendForApproval"
                type="button"
                :data-testid="`variant-card-${testidSuffix}-send-for-approval`"
                class="variant-card-send"
                :title="alreadySentForApproval
                    ? 'Send a reminder email to the client'
                    : ''"
                @click="onSendForApproval"
            >
                {{ alreadySentForApproval ? "Send reminder" : "Send for approval" }}
            </button>
        </div>
        </template>
    </article>
</template>

<script setup>
import { computed, nextTick, ref, watch } from "vue";
import { inkColorName } from "@/utils/inkColorName";
import { formatArtworkName } from "@/utils/formatArtworkName";
import { variantCardLabel } from "@/utils/variantCardLabel";
import { readabilityFor, routedColorsFor } from "@/utils/artworkReadability";
import {
    VARIANT_KIND_SINGLE_CATCH_ALL,
    VARIANT_KIND_CATCH_ALL_LIGHT,
    VARIANT_KIND_CATCH_ALL_DARK,
    VARIANT_KIND_SPECIFIC_COLORS,
    countLightColors,
    countDarkColors,
} from "@/utils/decorationRouting";
// The SAME classifier the variant resolver uses to route a colour to a
// light/dark card. Reused here so the representative garment colour we send
// to the backend is drawn from the very bucket this variant serves -- a
// second threshold would be two answers to one question.
import { classifyLDWithName } from "@/utils/luminance";
import { isEmbroiderySource } from "@/utils/artworkUploadContract";
import DecorationArtThumb from "@/components/decoration/DecorationArtThumb.vue";
import { colorwayMatchesRoute, resolveDefaultColorwayId } from "./defaultColorway";
import { variantThumbnailUrl } from "./variantThumbnail";
import { useVariantColorwayThumbnail } from "./useVariantColorwayThumbnail";

const props = defineProps({
    // PR-N4: row-stable identifier (server id, or _tempId for pending
    // rows). Replaces the legacy ``variantKey`` (A/B/S1) prop which
    // tracked the now-dropped variants.variant_key column. The user
    // never sees this value directly; it backs data-testids + parent
    // event payloads.
    variantId: { type: [String, Number], required: true },
    variant: { type: Object, required: true },
    placementId: { type: [String, Number], default: null },
    routedColorCount: { type: Number, default: 0 },
    isPreviewing: { type: Boolean, default: false },
    // Phase 1-A — resolved Artwork record (from the artworks Pinia store
    // via DecorationInspector). When null, the component falls back to
    // legacy variant-derived fields. Shape: {id, filename,
    // ink_color_hex, version, lifecycle_state, repository_id}.
    artwork: { type: Object, default: null },
    // Day 5 wiring (audit gap #2). Parent (DecorationInspector)
    // passes the per-placement lifecycle from productLifecycleForVariant
    // — the F-4 resolver already prefers real Artwork.lifecycle_state
    // over the interim hash. VariantCard's chip is driven by this prop;
    // no second lookup of artwork.lifecycle_state happens here because
    // the resolver lives at the placement level.
    lifecycle: { type: String, default: "approved" },
    // PR-L3 — product's currently-selected colors, used to render the
    // kind-aware "Routes: ..." subline. Each entry is at minimum
    // {color_sku} but {name, hex} are needed for the catch_all_light /
    // catch_all_dark counts (L/D classification via classifyLDWithName)
    // and for the specific_colors human-readable name list. Default to
    // an empty array so the subline degrades gracefully when the parent
    // hasn't wired this yet.
    productColors: { type: Array, default: () => [] },
    // Whether `productColors` is FINAL: the parent's supplier-catalog lookup
    // has finished (resolved, failed, or had nothing to fetch), so no hexes
    // beyond the ones present are coming. Until then a colour's hex may
    // still be null. Gates the one-shot colorway auto-default on shade
    // routes (see maybeAutoBindDefault). Defaults to true: a parent that
    // does not track a catalog has nothing further to wait for.
    productColorsSettled: { type: Boolean, default: true },
    // A split is binding this row's colorway right now (productEditor
    // `isColorwayBindPending`): the auto-default holds off.
    colorwayBindPending: { type: Boolean, default: false },
    // Shade-artwork UX polish -- combined presentation. Set by
    // DecorationInspector when this card stands in for the identical
    // catch_all_light + catch_all_dark pair (same artwork on both
    // rows). The card then presents itself as the placement's single
    // all-colors artwork: header "All colors (one artwork)", subline
    // "Routes: All colors (N)", neutral tint. Purely presentational --
    // the variant prop still carries the underlying (light) row.
    combinedShade: { type: Boolean, default: false },
    // Shade-artwork UX polish -- delete guard. Number of colors that
    // would have NO artwork for this placement if this variant were
    // deleted (computed by DecorationInspector via the resolver).
    // When > 0 the delete confirm names the gap concretely.
    strandedColorCount: { type: Number, default: 0 },
    // Slice 2 (editable variant cards) -- kind menu single-owner guard.
    // Map of catch-all kind -> the header label of the OTHER card that
    // already owns it on this placement (e.g.
    // { catch_all_light: "Front chest logo" }). The kind menu disables an
    // option present here (a card cannot claim a role another card holds).
    // The parent (DecorationInspector) computes this by scanning the
    // placement's variants excluding THIS one. Empty by default so the
    // menu is fully enabled when the parent hasn't wired it.
    ownedKindsByOther: { type: Object, default: () => ({}) },
    // Saved colorways for THIS placement's artwork version, as returned by
    // GET /api/wilcom/artwork-versions/<id>/colorways. Each row is at minimum
    // {id, name, substrate_target, threads}. The parent
    // (DecorationInspector) fetches them once per placement rather than per
    // card, since every variant on a placement shares one artwork version.
    //
    // Empty by default, which is the common case: most placements are not
    // embroidery and have no colorways at all. The control hides itself
    // entirely then (see showColorwayControl).
    colorways: { type: Array, default: () => [] },
    // Split / merge routes (card https://trello.com/c/vc8U5340).
    //
    // WHETHER A LIGHT ROW STANDS FOR ALL COLOURS IS A PROPERTY OF THE
    // SIBLING SET, not of this row -- the two-kind model has no third kind
    // to record it (`single_catch_all` is deliberately unselectable,
    // decoration_variants.py:764, and the delete path demotes a lone one
    // back to `catch_all_light`). This card can only see its OWN variant, so
    // the parent (DecorationInspector) computes the fact and passes it down.
    // Deriving it locally from `variant.kind` is impossible and guessing at
    // it would be a second answer to one question.
    standsForAllColors: { type: Boolean, default: false },
    // True on the LIGHT row of a light/dark pair -- the row that survives a
    // merge. Also parent-computed, for the same reason.
    canMergeShades: { type: Boolean, default: false },
    // An EMPTY SHADE: orders skip it (utils/emptyShade.js). The parent
    // decides -- it sees the siblings -- and the card warns.
    emptyShade: { type: Boolean, default: false },
});

const emit = defineEmits([
    "replace",
    "send-for-approval",
    "delete",
    // Slice 1 (editable variant cards): committed a rename. Payload
    // carries the new name (or null when cleared) so the parent
    // (DecorationInspector) can call the productEditor store's
    // renameVariant action. VariantCard stays dumb -- it does not talk
    // to the store directly.
    "rename",
    // Slice 2 (editable variant cards): the user picked a new routing
    // kind from the card kind menu. Payload:
    //   { variantId, placementId, kind }
    // The parent hands this to the store's setVariantKind action;
    // VariantCard stays dumb.
    "set-kind",
    // The user picked a colorway for this variant. Payload:
    //   { variantId, placementId, colorwayId, stopCount, garmentHex }
    // The parent hands this to the store's setVariantColorway action;
    // VariantCard stays dumb and never talks to the store directly.
    "set-colorway",
    // The user chose "Create a new colorway" in the picker. Payload:
    //   { variantId, placementId }
    // Deliberately NOT a bind: the parent resolves the artwork version and
    // routes to the decoration library's colorway editor. The card knows
    // which variant asked and nothing about routes.
    "create-colorway",
    // The user asked to SPLIT this all-colors route into light + dark.
    // Payload: { variantId, placementId }. The parent asks which shades the
    // decoration goes on (SplitShadesDialog) and dispatches the store's
    // splitPlacementShades action; the card stays dumb.
    "split-shades",
    // The user asked to MERGE the light/dark pair back to one all-colors
    // route. Payload: { variantId, placementId } naming the LIGHT row (the
    // survivor). The parent dispatches mergeVariantShades.
    "merge-shades",
    // The user clicked the decoration name. Payload: { variantId, artworkId }.
    // The parent navigates to the decoration in the library.
    "open-artwork",
]);

// ---------------------------------------------------------------------
// Slice 1 -- inline click-to-edit name
// ---------------------------------------------------------------------
const editingName = ref(false);
const nameDraft = ref("");
const nameInputRef = ref(null);
// Guards against the blur handler firing a second commit right after
// Enter/Escape already closed the editor (v-text-field blurs on unmount).
let nameCommitInFlight = false;

function onStartEditName() {
    // Combined-shade stand-in cards are presentational -- they do not
    // own a single renamable variant, so renaming is disabled there.
    if (props.combinedShade) return;
    nameDraft.value = customName.value;
    editingName.value = true;
    nameCommitInFlight = false;
    // Focus after the field mounts. `autofocus` covers most cases; this
    // is belt-and-suspenders for environments where autofocus is ignored.
    nextTick(() => {
        const el = nameInputRef.value;
        if (el && typeof el.focus === "function") el.focus();
    });
}

function onCancelEditName() {
    // Escape: discard the draft, restore the current name. Mark the
    // commit guard so the trailing blur (fired as the field unmounts)
    // does not commit the discarded draft.
    nameCommitInFlight = true;
    editingName.value = false;
    nameDraft.value = "";
}

function onCommitName() {
    if (nameCommitInFlight) return;
    nameCommitInFlight = true;
    editingName.value = false;

    const trimmed = (nameDraft.value ?? "").trim();
    // Empty commit clears the name to null (reverts to the kind label).
    const nextName = trimmed.length > 0 ? trimmed : null;
    const currentName = hasCustomName.value ? customName.value : null;

    // No-op when nothing changed -- do not emit a needless PATCH.
    if (nextName === currentName) {
        nameDraft.value = "";
        return;
    }

    emit("rename", {
        variantId: props.variantId,
        placementId: props.placementId,
        name: nextName,
    });
    nameDraft.value = "";
}

// Inline delete-confirm state — replaces the card body with a confirm
// prompt rather than mounting a modal, matching the 2026-06-13
// product-editor design handoff (editor-a-app.js variantConfirmCard).
const confirmingDelete = ref(false);

function onRequestDelete() {
    confirmingDelete.value = true;
}
function onCancelDelete() {
    confirmingDelete.value = false;
}
function onConfirmDelete() {
    // Leave the confirm overlay up until the parent removes us from
    // the variant list. If the delete errors out the parent will reset
    // its own state and re-render the card with the un-confirmed body.
    emit("delete", {
        variantId: props.variantId,
        placementId: props.placementId,
    });
}

// Task #70 (Path B glue): show the Send-for-approval button only when
// the variant has an artwork_file_id. That id IS the
// decoration_artifacts row id the modal will post to
// /api/decoration-artifacts/:id/send-for-approval, so when it's null
// there is nothing to send.
const canSendForApproval = computed(
    () => props.variant && props.variant.artwork_file_id != null,
);

// Issue #2179 Problem 1: the artwork has ALREADY been sent for approval
// when its lifecycle is one of the in-flight approval states. In that
// case the Send button reads "Send reminder" instead of "Send for
// approval" (same action -- re-opens the same modal, same event). The
// lifecycle arrives via the placement-level ``lifecycle`` prop
// (DecorationInspector.placementLifecycle), which the F-4 resolver
// drives from the real Artwork.lifecycle_state.
const SENT_FOR_APPROVAL_STATES = Object.freeze([
    "awaiting_approval",
    "in_review",
]);
const alreadySentForApproval = computed(() =>
    SENT_FOR_APPROVAL_STATES.includes(props.lifecycle),
);

// PR-N4: kind-aware header label. Replaces the legacy "Variant A"/
// "Variant B" framing -- the user-facing wording reads from
// variant.kind directly. For specific_colors we show "Specific colors"
// since the routed list lives on the subline already.
//
// KIND_HEADER_LABELS + the header-priority logic now live in the shared
// helper ``@/utils/variantCardLabel`` so the store-editor Decorations
// rollup (StoreDecorations.vue) and this product-editor card show the
// SAME color-card names -- a single source of truth, no drift.

// Slice 1 (editable variant cards): a card can carry an optional custom
// name that replaces the kind-derived header label. Blank / whitespace-
// only names are treated as "no custom name" (the backend validator
// coerces "" -> NULL, and this trim mirrors it so an all-whitespace draft
// never counts as custom locally either).
const customName = computed(() => {
    const raw = props.variant?.name;
    if (typeof raw !== "string") return "";
    return raw.trim();
});
const hasCustomName = computed(() => customName.value.length > 0);

// Combined presentation always speaks for all colors, so it keeps its
// fixed "All colors (one artwork)" label even when the underlying (light)
// row carries a custom name -- the combined card is a presentational
// stand-in for the pair, not a single renamable variant.
// Delegates to the shared helper so the header priority (combined-shade
// label > custom name > kind label > "Artwork" fallback) is defined in
// exactly one place. ``customName`` above is still used independently for
// the edit-in-place draft/no-op logic, so it stays.
const headerText = computed(() =>
    variantCardLabel(props.variant, {
        combinedShade: props.combinedShade,
        standsForAllColors: props.standsForAllColors,
    }),
);

// Slice 1: role chip beside a custom name. The chip carries the routing
// role word the header no longer shows once a custom name is set, so the
// distributor always sees which colors the artwork applies to. Rendered
// ONLY when a custom name is set (when unset, the header itself carries
// the role word, so a chip would be redundant).
const KIND_ROLE_CHIP_LABELS = Object.freeze({
    catch_all_light: "Light",
    catch_all_dark: "Dark",
    single_catch_all: "All colors",
    specific_colors: "Custom",
});
const roleChipLabel = computed(() => {
    // A lone light row routes every colour, so its ROLE word is "All
    // colors" -- the chip exists to keep the routing role visible once a
    // custom name hides it from the header, and "Light" would misstate it.
    if (props.standsForAllColors && !props.combinedShade) return "All colors";
    return KIND_ROLE_CHIP_LABELS[props.variant?.kind] ?? "Artwork";
});

// ---------------------------------------------------------------------
// Slice 2 -- mutable kind menu (exactly three options)
// ---------------------------------------------------------------------
// The card kind menu lets the distributor re-route this artwork to All
// light colors or All dark colors. single_catch_all is NOT offered
// (existing single rows stay functional but uncreatable -- converged
// design doc 3.3/6.2), and neither is specific_colors (MVP is
// light/dark only, Jake 2026-08-24; existing rows still resolve via the
// backend tier, they are just no longer authorable). At most one light
// + one dark card per placement: an option owned by ANOTHER card is
// disabled with a hint.
const kindMenuOpen = ref(false);

// The kind menu is HIDDEN on a lone light row (card
// https://trello.com/c/vc8U5340). Its two options are "All light colors" --
// which is what the row already is, only stated in a way that no longer
// matches what it routes -- and "All dark colors", which would silently
// strand every light colour with no artwork at all. Split is the affordance
// that expresses the real intent ("I want a different artwork on darks"),
// and it appears in the menu's place. Combined-shade stand-ins hide it for
// the older reason: they speak for a PAIR, not one mutable variant.
const showKindMenu = computed(
    () => !props.combinedShade && !props.standsForAllColors,
);

// The three selectable kinds, in menu order.
const KIND_MENU_OPTIONS = Object.freeze([
    { kind: VARIANT_KIND_CATCH_ALL_LIGHT, label: "All light colors" },
    { kind: VARIANT_KIND_CATCH_ALL_DARK, label: "All dark colors" },
]);

// A catch-all option is disabled when another card owns it. The card's
// OWN current kind is never disabled (re-selecting it is a harmless
// no-op / blocked upstream).
const kindMenuItems = computed(() => {
    const owned = props.ownedKindsByOther || {};
    return KIND_MENU_OPTIONS.map((opt) => {
        let disabled = false;
        let hint = "";
        if (
            (opt.kind === VARIANT_KIND_CATCH_ALL_LIGHT
                || opt.kind === VARIANT_KIND_CATCH_ALL_DARK)
            && owned[opt.kind]
        ) {
            disabled = true;
            hint = `Already used by ${owned[opt.kind]}`;
        }
        const isCurrent = props.variant?.kind === opt.kind;
        return { ...opt, disabled, hint, isCurrent };
    });
});

function onOpenKindMenu() {
    // Combined-shade stand-in cards are presentational -- they speak for
    // a light+dark pair, not a single mutable variant, so the kind menu
    // is disabled there (matching the rename block). A lone light row is
    // excluded for the reason on showKindMenu.
    if (!showKindMenu.value) return;
    kindMenuOpen.value = true;
}

function onPickKind(item) {
    if (!item || item.disabled) return;
    kindMenuOpen.value = false;
    // Re-selecting the current kind is a no-op (nothing to change).
    if (item.isCurrent) return;

    // Catch-all target: no color_skus (the backend clears them).
    emit("set-kind", {
        variantId: props.variantId,
        placementId: props.placementId,
        kind: item.kind,
    });
}

// ---------------------------------------------------------------------
// Colorway binding -- the product-editor half of the colour model
// ---------------------------------------------------------------------
//
// Card: Embroidery colorway editing (MUST-SHIP) https://trello.com/c/2hcgDM7J
//
// WHY THE DEFAULT LIVES HERE AND THE DECLARATION LIVES IN THE DECO LIBRARY
// (Jake, 2026-08-25). A decoration is reused across many products in many
// stores, so at authoring time nobody knows which garment colours it will
// land on. The library can therefore only make the ABSTRACT claim -- "this
// colorway is for light garments" / "for dark" / no claim at all. This card
// is the first place where the real colours are known, so this is where the
// abstract claim gets resolved into a concrete suggestion.
//
// The resolution is deliberately weak. It produces a pre-selection, and every
// option remains pickable: see the `colorwayOptions` comment below.

const colorwayMenuOpen = ref(false);

// Which substrate a variant's kind implies. `specific_colors` and
// `single_catch_all` route to a MIX of light and dark garments, so no single
// substrate is implied and they get no suggestion at all -- silence is the
// honest answer, and a coin-flip default here would be sewn onto real shirts.
const KIND_TO_SUBSTRATE = Object.freeze({
    [VARIANT_KIND_CATCH_ALL_LIGHT]: "light",
    [VARIANT_KIND_CATCH_ALL_DARK]: "dark",
});

const impliedSubstrate = computed(
    () => KIND_TO_SUBSTRATE[props.variant?.kind] ?? null,
);

const availableColorways = computed(() =>
    Array.isArray(props.colorways) ? props.colorways.filter((c) => c && c.id != null) : [],
);

// Hidden outright when there is nothing to pick. An empty menu is a dead end
// that reads as a broken feature rather than an inapplicable one. Combined-
// shade cards opt out for the same reason rename and the kind menu do: the
// card stands in for a light+dark PAIR, so a single binding is ambiguous.
// The artwork's real filename, in the order that preserves the FORMAT
// signal. `original_filename` is the name of the file that was actually
// uploaded and therefore carries the true extension; `filename` is the
// distributor-chosen display name and may carry none. The variant's own
// `artwork_filename` is the last resort, for pre-Phase-F rows and
// in-flight artworks-store loads where no Artwork has resolved yet.
//
// Same order AddPlacementWizard's method lock uses (`art.original_filename
// || art.filename`) -- one rule for "what file is this", not two.
const artworkSourceName = computed(() => {
    const candidates = [
        props.artwork?.original_filename,
        props.artwork?.filename,
        props.variant?.artwork_filename,
    ];
    for (const c of candidates) {
        if (typeof c === "string" && c.trim()) return c.trim();
    }
    return null;
});

// The same name with the '#<guid>' identity suffix removed, via the shared
// formatter. extensionOf() takes the LAST dot -- so "logo.dst#e43d..." would
// parse as extension ".dst#e43d...", match nothing, and silently cost a
// genuine embroidery decoration its picker. Also what the card DISPLAYS.
const artworkSourceNameForFormat = computed(() => {
    return formatArtworkName(artworkSourceName.value) || null;
});

// Is this decoration embroidery? Keyed off the file format via the SHARED
// helper (extension in {.dst, .emb}), which mirrors the backend authority
// api/src/services/artwork_format.py::EMBROIDERY_SOURCE_EXTENSIONS. A
// stitch file can only be embroidery by definition, and the backend has
// already reconciled a spoofed extension against a magic-byte sniff before
// the name reaches us -- so the extension is the trustworthy signal here.
//
// Unknown format (nothing names the file) reads as NOT embroidery. That is
// the honest default: see the comment on showColorwayControl.
const isEmbroideryVariant = computed(() =>
    isEmbroiderySource(artworkSourceNameForFormat.value),
);

// The colorway control is EMBROIDERY-ONLY (Jake, 2026-08-29: "non-embroidery
// doesn't have a picker at all").
//
// A colorway is a THREAD MAPPING -- a stop in a stitch file bound to a
// thread colour. A raster or vector decoration has no stops, so there is
// nothing to map and the concept does not apply. Rendering an empty picker
// would assert that it does apply and merely has not been filled in, which
// is a false claim; absent is the only honest presentation.
//
// This guard reads the FORMAT, not `colorways.length`. The length test was
// a proxy that agreed with the format only by coincidence -- non-embroidery
// artwork has no colorway rows today -- and would render the control the
// moment anything handed this card a non-empty array for a raster (a stale
// parent cache, or a placement whose artwork was replaced .dst -> .png).
// The emptiness check is KEPT as well: an embroidery artwork with no saved
// colorways yet has nothing to pick, so the picker would be a dead end.
//
// SHOWN ON THE COMBINED CARD TOO (Jake, 2026-10-21: "the color cards on the
// right should have a drop-menu that lets me choose colorways"). The
// combined card is what a new placement shows -- one decoration on every
// color -- so a pick there means ONE colorway for every color, and the
// inspector binds it to both rows of the pair. It still never AUTO-binds:
// see maybeAutoBindDefault.
const showColorwayControl = computed(
    () =>
        isEmbroideryVariant.value &&
        availableColorways.value.length > 0,
);

// The colorway currently bound to this variant, read from the SNAPSHOT.
// `colorway_id` is provenance only -- deliberately NOT traversed to the live
// row (ADR 0044), so nothing here can render a value the variant does not
// actually sew.
const boundColorwayId = computed(() => props.variant?.colorway_id ?? null);

const boundColorway = computed(() => {
    const threads = props.variant?.colorway_threads_json;
    if (!Array.isArray(threads) || threads.length === 0) return null;
    return {
        threads,
        threadCountLabel:
            threads.length === 1 ? "1 thread" : `${threads.length} threads`,
    };
});

// THE DEFAULT, and why nothing here disables anything.
//
// `isDefault` marks the colorway this variant defaults to. It drives the
// "Suggested" hint AND -- since 2026-09-02 -- the automatic binding below.
//
//   1. STILL TRUE: no option is ever disabled. A distributor can bind a
//      light-declared colorway to a dark variant. The library declared
//      "light" without knowing this product's colours; the person looking at
//      the actual colours may have a reason it could not have. Disabling the
//      mismatch would silently promote an advisory declaration into a rule.
//
//   2. REVERSED, by Jake on 2026-09-02, after driving this surface on 8089.
//      This comment previously read: "When no colorway declares a matching
//      substrate, NOTHING is suggested. An undeclared colorway is 'works on
//      both', not 'use me by default', so promoting one would manufacture an
//      opinion the author declined to give." A test pinned it, named
//      "suggests nothing when no colorway declares a matching substrate",
//      whose comment was "Silence beats a confident wrong guess."
//
//      Jake's ruling replaces it with a fallback: match the colour route
//      first, and if nothing matches, take the first available colorway of
//      ANY substrate. The original argument was made before anyone had used
//      the surface; his was made after using it, and he judged an
//      approximate visible default better than an empty one -- the picker
//      stays fully overridable, so the declaration is still advisory.
//
//      THE HISTORY IS KEPT DELIBERATELY so a later reader does not "restore"
//      the old behaviour thinking it was lost by accident. It was not; it
//      was overruled. The inverted test lives in
//      VariantCard.autodefault.spec.js under a name that says so.
//
// Resolution itself lives in `defaultColorway.js` -- one ladder, shared with
// wherever a variant is created, rather than a second copy here that could
// drift.
// The colour route the colorway default is resolved for. The combined card
// covers EVERY color, so its route is all colors: the colorway declared for
// all garments is its match, not the light row's (Jake, 2026-10-21).
const colorwayRouteKind = computed(() =>
    props.combinedShade ? VARIANT_KIND_SINGLE_CATCH_ALL : props.variant?.kind,
);

const colorwayOptions = computed(() => {
    const suggested = resolveDefaultColorwayId({
        kind: colorwayRouteKind.value,
        colorways: availableColorways.value,
        // Jake, 2026-09-02: "the default colorway picked for light garment or
        // dark garment should be the one whose contrast against the substrate
        // is most appropriate." The resolver ranks the substrate-matching
        // colorways by real ink-vs-garment contrast, so it needs the colours
        // this variant actually prints on.
        //
        // `routedColorsFor` is the SAME resolver the readability badge and the
        // "Routes: ..." subline already use, so the default and the badge
        // cannot disagree about which garments are in scope. Passing
        // `props.productColors` raw would score a light-route variant against
        // dark garments it never touches.
        garmentColors: routedColorsFor(props.variant, props.productColors),
    });
    return availableColorways.value.map((c) => ({
        id: c.id,
        name: c.name || `Colorway ${c.id}`,
        substrateTarget: c.substrate_target ?? null,
        threads: Array.isArray(c.threads) ? c.threads : [],
        isDefault: c.id === suggested,
        // Declared for this route (rung 1), as opposed to the ladder's
        // any-substrate fallback. The combined card auto-binds only this
        // (#687); see maybeAutoBindDefault.
        matchesRoute: colorwayMatchesRoute({
            kind: colorwayRouteKind.value,
            colorway: c,
        }),
        isSelected: boundColorwayId.value != null && c.id === boundColorwayId.value,
    }));
});

const colorwayTriggerLabel = computed(() => {
    if (boundColorway.value) {
        const sel = colorwayOptions.value.find((o) => o.isSelected);
        // Name the bound colorway when we can still see it in the list; fall
        // back to neutral wording when we cannot (it may have been deleted,
        // which nulls the FK but LEAVES the snapshot -- the variant still
        // sews those threads, so the card must not claim nothing is set).
        return sel ? sel.name : "Custom colors";
    }
    // #687: "Thread colors" did not read as "nothing chosen". Nothing is
    // bound, so the preview sews the stitch file's own embedded colors.
    return "File colors (none chosen)";
});

// #687: the combined card says plainly that nothing is chosen, and what to
// do about it. Only when it will NOT bind a default itself -- an all-colors
// colorway exists and is being bound, so "nothing chosen" would be wrong a
// moment later. Combined card only: "Split light / dark" sits beside it.
const showUnboundColorwayPrompt = computed(
    () =>
        props.combinedShade &&
        showColorwayControl.value &&
        boundColorwayId.value == null &&
        !boundColorway.value &&
        !colorwayOptions.value.some((o) => o.isDefault && o.matchesRoute),
);

function colorwayThreadTitle(thread) {
    if (!thread) return "";
    const parts = [];
    if (thread.code) parts.push(String(thread.code));
    if (thread.hex) parts.push(String(thread.hex));
    return parts.join(" ");
}

// The garment colour a non-fixed slot resolves against.
//
// The backend resolves `match_garment` / `contrast_auto` slots against ONE
// concrete colour, but a catch-all variant routes to MANY. We send a
// REPRESENTATIVE colour of the bucket this variant serves, taken from the
// product's actually-selected colours, rather than a hardcoded black/white:
// the resolver's own light/dark classification is what routes colours to this
// variant in the first place, so a colour from that bucket is guaranteed to
// classify the way the variant expects.
//
// null when the product has no colours of this shade yet, or when the variant
// serves a mixed bucket. The backend treats null as "unknown" and REFUSES to
// resolve a garment-dependent slot rather than guessing -- which is the right
// outcome: a thread colour computed from a guessed garment gets sewn.
const representativeGarmentHex = computed(() => {
    const want = impliedSubstrate.value;
    if (!want) return null;
    const rows = Array.isArray(props.productColors) ? props.productColors : [];
    for (const row of rows) {
        if (!row || !row.hex) continue;
        const { ld } = classifyLDWithName({
            hex: row.hex ?? null,
            name: row.name ?? null,
        });
        if (ld === want) return row.hex;
    }
    return null;
});

function onPickColorway(opt) {
    if (!opt) return;
    colorwayMenuOpen.value = false;
    // Re-picking the bound colorway is a no-op -- do not spend a paid Artifi
    // render (the bind endpoint buys an on-garment composite on every
    // successful bind, against a hard annual cap) to arrive where we already
    // are.
    if (opt.isSelected) return;

    emit("set-colorway", {
        variantId: props.variantId,
        placementId: props.placementId,
        colorwayId: opt.id,
        // The slot count of the colorway being bound. The backend refuses a
        // count mismatch; sending the picked colorway's own length is honest
        // rather than circular, because the picker is drawn from THIS
        // placement's artwork version and the backend independently re-checks
        // colorway.artwork_version_id against the placement's decoration --
        // that version check, not this number, is what closes the "authored
        // against different geometry" hazard.
        stopCount: opt.threads.length,
        garmentHex: representativeGarmentHex.value,
    });
}

// AUTOMATIC DEFAULT BINDING (Jake, 2026-09-02).
//
// "when a variant is created, it should default to the first colorway that
// matches the color routes". Jake chose a real DEFAULT over an advisory
// badge, and accepted its known cost: the system makes a choice the user may
// not notice, and "no colorway chosen" becomes indistinguishable from "this
// colorway chosen". He explicitly declined a provenance column to tell them
// apart, so there is deliberately nothing here recording that the choice was
// automatic.
//
// WHY THIS IS CAREFUL RATHER THAN A ONE-LINER. A bind is NOT a column write:
// `POST .../set_colorway` resolves the threads server-side and then BUYS AN
// ARTIFI ON-GARMENT COMPOSITE, which counts against a hard annual cap
// (decoration_variants.py `_spend_on_garment_composite`). Every guard below
// exists to keep that spend equal to what a human picking once would cost.
//
//   * `_autoBindAttempted` makes it fire ONCE per mounted card. A re-render
//     must not re-spend to arrive where we already are -- the same reason
//     `onPickColorway` short-circuits a re-pick.
//   * `boundColorwayId != null` means a colorway is already chosen, so we
//     never overwrite it. That is the no-clobber guarantee: a user's own
//     pick always survives resolution running again.
//   * `showColorwayControl` encodes an embroidery decoration (a raster has
//     no stops to map) with at least one colorway to choose from. It is SHOWN
//     on the combined card too (Jake, 2026-10-21). The combined card stands
//     in for a light+dark PAIR, so it auto-binds ONLY a colorway declared for
//     all colors -- the exact route match the menu marks Suggested (#687,
//     Jake's card ask). The ladder's any-substrate fallback stays a hint
//     there: one shade's colorway on both shades would be a guess. The
//     inspector fans the emit out to both rows (onSetVariantColorway).
//   * `colorwayBindPending` holds the bind while a split is writing this
//     row's colorway itself, so there is one writer, not two.
//
//   * `awaitingGarmentHexes` holds the bind on a SHADE route until the
//     contrast ranking can actually be computed (below).
//
// It emits the SAME `set-colorway` event a manual pick emits, so there is
// ONE write path. A separate one could drift from the hand-picked shape --
// and the payload is where the server's stop-count check lands.
let _autoBindAttempted = false;

// WAIT FOR THE GARMENT HEXES on a shade route (fixed 2026-10-21).
//
// Light/dark defaults are ranked by ink-vs-garment contrast, which needs the
// routed garments' hexes -- and those come from the supplier catalog, which
// lands AFTER the cards mount. Until it does, the parent's product colours
// are `{hex: null}`, the ranking is unscorable, and it falls back to lowest
// id. Because the latch above makes the bind one-shot, binding in that
// window made the fallback PERMANENT: on 8090 both cards bound the
// unreadable ink (white on White, dark on Navy) 14 ms before the catalog
// response arrived.
//
// So a shade-route bind waits until EITHER every routed garment has a hex
// (the ranking is computable now) OR the parent reports the colours settled
// (no more hexes are coming -- a failed catalog, or a supplier with none).
// Settled-with-no-hexes still binds, at lowest id: a product whose catalog
// never answers must not stay unbound forever.
//
// All-colours and specific-colour routes are NOT ranked by contrast, so
// hexes cannot change their answer and they do not wait.
const awaitingGarmentHexes = computed(() => {
    if (!impliedSubstrate.value) return false;
    if (props.productColorsSettled) return false;
    const routed = routedColorsFor(props.variant, props.productColors);
    return routed.length === 0 || routed.some((c) => !c?.hex);
});

function maybeAutoBindDefault() {
    if (_autoBindAttempted) return;
    if (!showColorwayControl.value) return;
    // Already chosen -- by a human or by a previous session. Never clobber.
    if (boundColorwayId.value != null) return;
    // A combined card still sewing a snapshot whose colorway was deleted
    // ("Custom colors") was a choice too; do not replace it.
    if (props.combinedShade && boundColorway.value) return;
    if (awaitingGarmentHexes.value) return;
    // A split is binding this row's colorway itself: it is the only writer.
    // Not latched, so if the split binds nothing for this shade the default
    // still runs once the flag clears.
    if (props.colorwayBindPending) return;

    const opt = colorwayOptions.value.find((o) => o.isDefault);
    // No default resolvable (Jake's rung 3: no colorways at all) -> leave it
    // to the existing all-black render rather than binding nothing.
    if (!opt) return;
    // The combined card binds only an all-colors colorway (#687). Not
    // latched: if one is added later, the card can still adopt it.
    if (props.combinedShade && !opt.matchesRoute) return;

    // Latch BEFORE emitting: the parent's response re-renders this card, and
    // latching afterwards would leave a window for a second emit.
    _autoBindAttempted = true;
    emit("set-colorway", {
        variantId: props.variantId,
        placementId: props.placementId,
        colorwayId: opt.id,
        stopCount: opt.threads.length,
        garmentHex: representativeGarmentHex.value,
    });
}

// Immediate, because a card that mounts already needing a default should not
// wait for an unrelated change to trigger one. The watch (rather than a bare
// call) covers the real case where `colorways` arrive AFTER mount -- the
// decoration-library fetch is async, so on first render there is often
// nothing to resolve against yet. `awaitingGarmentHexes` is watched so the
// held bind fires the moment the catalog settles, even when that settle
// changes nothing else the card reads (a failed fetch publishes no colours).
watch(
    () => [
        showColorwayControl.value,
        boundColorwayId.value,
        colorwayOptions.value,
        awaitingGarmentHexes.value,
        props.colorwayBindPending,
    ],
    maybeAutoBindDefault,
    { immediate: true },
);

// Create-new. Closes the menu and hands off -- it must NEVER reach the bind
// path, because a successful bind buys an Artifi composite against a hard
// annual cap and nothing has been chosen yet.
function onCreateColorway() {
    colorwayMenuOpen.value = false;
    emit("create-colorway", {
        variantId: props.variantId,
        placementId: props.placementId,
    });
}

const emptyShadeName = computed(() =>
    props.variant?.kind === VARIANT_KIND_CATCH_ALL_LIGHT ? "light" : "dark",
);
const emptyShadeNameCap = computed(() =>
    emptyShadeName.value === "light" ? "Light" : "Dark",
);

// The library artwork this card's decoration opens, or null. Only a card
// that carries a decoration AND resolved to a library Artwork has one: an
// empty route (no decoration on this shade) or an unresolved legacy row
// renders its name as plain text rather than a link to nowhere.
const libraryArtworkId = computed(() => {
    if (!props.variant?.artwork_file_id) return null;
    const id = props.artwork?.id;
    return id == null ? null : id;
});

function onOpenArtwork() {
    if (libraryArtworkId.value == null) return;
    emit("open-artwork", {
        variantId: props.variantId,
        artworkId: libraryArtworkId.value,
    });
}

// Split / merge routes. Both stay dumb emits -- the parent owns the store
// dispatch AND the colorway list the auto-selection needs, neither of which
// this card can see.
function onSplitShades() {
    emit("split-shades", {
        variantId: props.variantId,
        placementId: props.placementId,
    });
}

function onMergeShades() {
    emit("merge-shades", {
        variantId: props.variantId,
        placementId: props.placementId,
    });
}

// Shade-artwork UX polish -- delete confirm copy. When deleting this
// variant would leave colors with no artwork on this placement, name
// the gap concretely (count + shade bucket). Otherwise keep the
// lighter fallback copy.
const strandedShadeNoun = computed(() => {
    // Combined mode (P1-1): the card stands in for BOTH shade rows, so
    // the gap spans both buckets -- plain "colors", never the
    // underlying (light) row's shade.
    if (props.combinedShade) return "color";
    const kind = props.variant?.kind;
    if (kind === VARIANT_KIND_CATCH_ALL_DARK) return "dark color";
    if (kind === VARIANT_KIND_CATCH_ALL_LIGHT) return "light color";
    return "color";
});

const deleteConfirmBody = computed(() => {
    const n = props.strandedColorCount ?? 0;
    if (n > 0) {
        const noun = strandedShadeNoun.value;
        return (
            `${n} ${noun}${n === 1 ? "" : "s"} will have no artwork `
            + "for this placement. Historical orders are preserved."
        );
    }
    return (
        "Colors routed to it will fall back to the next tier "
        + "(or show no preview if no fallback exists). Historical "
        + "orders are preserved."
    );
});

// Kind-aware card background tint (locked design decision 2026-06-21).
// The tint communicates routing at a glance:
//   catch_all_light  -> subtle cool light  (light-garment artwork card)
//   catch_all_dark   -> subtle dark gray-blue (dark-garment artwork card,
//                       mirrors design-handoff .vcard.dark #1a2332)
//   specific_colors  -> neutral (current white background)
//   single_catch_all -> neutral (current white background)
//   missing / unknown -> neutral (defensive fallback)
// Reference: planning/design-handoffs/handoff_2026-06-13/2026-06-13-product-editor/
// design/editor-a.css (.vcard.dark rule).
const KIND_BG_CLASS = Object.freeze({
    catch_all_light: "variant-card-bg-light",
    catch_all_dark: "variant-card-bg-dark",
    specific_colors: "variant-card-bg-neutral",
    single_catch_all: "variant-card-bg-neutral",
});
const kindBgClass = computed(() => {
    // Combined presentation is shade-agnostic: neutral tint.
    if (props.combinedShade) return "variant-card-bg-neutral";
    return KIND_BG_CLASS[props.variant?.kind] ?? "variant-card-bg-neutral";
});

// data-testid suffix: prefer the stable variant id (string-coerced for
// template interpolation). Tests that previously relied on
// "variant-card-A" or "variant-card-S1" now key off the row id, which
// is the only durable identifier post-PR-N4.
const testidSuffix = computed(() => String(props.variantId));

const variantAltLabel = computed(() => headerText.value);

const colorCountLabel = computed(() => {
    const n = props.routedColorCount ?? 0;
    return `${n} ${n === 1 ? "color" : "colors"}`;
});

// ---------------------------------------------------------------------
// (Removed 2026-08-24) The Slice 3 toggle-colors dialog lived here. It
// authored a specific_colors card's explicit color_skus set. MVP
// colorization is light/dark only, so there is no per-SKU set to
// author and the dialog + its "set-colors" emit are gone.

// PR-L3 — kind-aware "Routes: ..." subline.
//
// The subline mirrors the resolver order in
// api/src/endpoints/decoration_variants.py:variant_for_color so the user
// sees exactly which color set the catch-all or specific override
// targets. Lookups are name-based against props.productColors, falling
// back to color_sku when no name is on the list. Catch-all counts use
// the same classifyLDWithName pipeline DecorationInspector uses to
// route in the live preview, so the number under the subline matches
// what they see in the filmstrip.
const SUBLINE_MAX_INLINE = 4;

const routesSublineBuild = computed(() => {
    // Combined presentation. Gate fix round (PR #1915 P1-2): the count
    // comes from routedColorCount -- the parent's light+dark pair sum
    // -- NOT productColors.length. A specific_colors sibling claims
    // colors away from the pair; when that happens the label degrades
    // honestly to "All other colors (N)".
    if (props.combinedShade) {
        const total = (props.productColors || []).length;
        const n = props.routedColorCount ?? 0;
        const label = n < total ? "All other colors" : "All colors";
        return { text: `Routes: ${label} (${n})`, title: '' };
    }
    const kind = props.variant?.kind;
    if (!kind) return null;

    // Build a quick color-sku -> name map. Fall through to the SKU
    // verbatim when a product color row is missing.
    const colorBySku = new Map();
    for (const c of props.productColors || []) {
        if (c && typeof c.color_sku === 'string') {
            colorBySku.set(c.color_sku, c);
        }
    }
    function nameFor(sku) {
        const row = colorBySku.get(sku);
        return (row && typeof row.name === 'string' && row.name.trim())
            ? row.name.trim()
            : sku;
    }

    if (kind === VARIANT_KIND_SINGLE_CATCH_ALL) {
        const n = (props.productColors || []).length;
        return { text: `Routes: All colors (${n})`, title: '' };
    }
    if (kind === VARIANT_KIND_CATCH_ALL_LIGHT) {
        // A lone light row (no dark sibling, no specific_colors sibling) is
        // the all-colours state, so the subline counts EVERY product colour.
        // Reporting only the light ones would understate what this artwork
        // is actually sewn onto -- the dark shirts get it too.
        if (props.standsForAllColors) {
            const n = (props.productColors || []).length;
            return { text: `Routes: All colors (${n})`, title: '' };
        }
        const n = countLightColors(props.productColors);
        return { text: `Routes: All light colors (${n})`, title: '' };
    }
    if (kind === VARIANT_KIND_CATCH_ALL_DARK) {
        const n = countDarkColors(props.productColors);
        return { text: `Routes: All dark colors (${n})`, title: '' };
    }
    if (kind === VARIANT_KIND_SPECIFIC_COLORS) {
        const skus = Array.isArray(props.variant.color_skus)
            ? props.variant.color_skus
            : [];
        if (skus.length === 0) {
            // Degenerate: a specific_colors variant with no SKUs bound.
            // Show a neutral label so the surface stays consistent.
            return { text: 'Routes: No colors yet', title: '' };
        }
        const names = skus.map(nameFor);
        if (names.length <= SUBLINE_MAX_INLINE) {
            return { text: `Routes: ${names.join(', ')}`, title: names.join(', ') };
        }
        const head = names.slice(0, SUBLINE_MAX_INLINE);
        const rest = names.length - SUBLINE_MAX_INLINE;
        return {
            text: `Routes: ${head.join(', ')}, +${rest} more`,
            // Hover/long-press reveals the full list.
            title: names.join(', '),
        };
    }
    return null;
});

const routesSubline = computed(() => routesSublineBuild.value?.text ?? null);
const routesSublineTitle = computed(
    () => routesSublineBuild.value?.title ?? null,
);

// Thumbnail: the BOUND COLORWAY's picture when there is one, else the
// version's default preview (variantThumbnail.js, shared with the split
// dialog). Jake, 2026-10-22: the card must show the design in the threads it
// sews, not the default (black) stitch render.
//
// Asked for only when it can mean something: an embroidery decoration (a
// raster has no stops to recolor), a bound snapshot, and ids the server knows
// (a staged `tmp-` row has not been persisted). The snapshot is passed only to
// key the client cache; the server derives the colorway from its own copy.
const colorwayThumbSource = computed(() => {
    if (!isEmbroideryVariant.value || !boundColorway.value) return null;
    if (!props.variant?.artwork_file_id) return null;
    const placementId = String(props.placementId ?? "");
    const variantId = String(props.variantId ?? "");
    if (!/^\d+$/.test(placementId) || !/^\d+$/.test(variantId)) return null;
    return {
        placementId,
        variantId,
        artworkFileId: String(props.variant.artwork_file_id),
        threads: boundColorway.value.threads,
    };
});
const {
    url: colorwayThumbUrl,
    state: colorwayThumbState,
    markBroken: markColorwayThumbBroken,
} = useVariantColorwayThumbnail(colorwayThumbSource);

const thumbnailUrl = computed(
    () => colorwayThumbUrl.value || variantThumbnailUrl(props.variant, props.artwork),
);

// The colorway URL failed to load in the browser: fall back to the default
// picture rather than leave a broken image up.
function onThumbError() {
    if (colorwayThumbUrl.value) markColorwayThumbBroken();
}

const artworkName = computed(() => {
    // Strip the '#<guid>' identity suffix at display time only -- the
    // stored value (artwork_filename / variant_sku) keeps its full form
    // for identity/lookup (Jake 2026-07-10). Fall through to the next
    // source when a raw value formats to empty (e.g. a leading-'#' name).
    const raw = props.variant.artwork_filename || props.variant.variant_sku;
    return formatArtworkName(raw) || "Untitled artwork";
});

// Filename — Phase 1-A: prefer a name that actually names the file.
// `artworkSourceName` already encodes that preference order (real
// Artwork's original_filename, then its display filename, then the
// variant's artwork_filename).
//
// The last resort is the last 8 chars of artwork_file_id, the derivation
// that pre-dates Phase F. It used to append ".emb" — a hardcode that
// displayed a stitch-file extension on EVERY variant, so a PNG decoration
// claimed to be embroidery (Jake, 2026-08-29: "We shouldn't be hardcoding
// '.emb'"). artwork_file_id is an opaque identifier, not a filename, so
// there is no extension to recover from it; inventing one is a guess that
// is wrong for most placements. We show the identifier alone instead —
// unhelpful is better than untrue, and the honest fix is to give this card
// a real filename rather than a better guess.
const filename = computed(() => {
    const named = artworkSourceNameForFormat.value;
    if (named) return named;

    const id = props.variant.artwork_file_id;
    if (!id) return "—";
    const s = String(id);
    return s.length > 8 ? s.slice(-8) : s;
});

// Ink hex — prefer the real Artwork's ink_color_hex when present, fall
// back to the variant's column (legacy). Default white on both misses.
const inkHex = computed(() => {
    const real = props.artwork?.ink_color_hex;
    if (typeof real === "string" && real.trim()) return real;
    return props.variant.ink_color_hex || "#ffffff";
});

const inkDotStyle = computed(() => ({
    background: inkHex.value,
}));

// Ink label — Phase 1-A: real Artwork goes through the broader
// inkColorName lookup (canonical decorator-floor palette). When no
// Artwork is resolved we keep the pre-Phase-F 2-entry hardcode to
// preserve the interim behavior for products without seeded Artwork
// rows.
const inkLabel = computed(() => {
    if (props.artwork?.ink_color_hex) {
        const named = inkColorName(props.artwork.ink_color_hex);
        if (named) return named;
    }
    // Legacy fallback: 2-entry map preserved for compatibility with
    // pre-Phase-F seed data and interim fixtures.
    const hex = String(inkHex.value).toLowerCase();
    if (hex === "#ffffff" || hex === "#fff") return "White";
    if (hex === "#0a1b3d" || hex === "#000000" || hex === "#000") return "Navy";
    return hex;
});

// Version label — Phase 1-A: prefer artwork.version (integer from the
// Artwork row). Fall back to "v1" interim hardcode when no Artwork is
// resolved (preserves the pre-Phase-F interim behavior).
const versionLabel = computed(() => {
    const v = props.artwork?.version;
    if (typeof v === "number" && Number.isFinite(v)) return `v${v}`;
    if (typeof v === "string" && v.trim()) return `v${v.trim()}`;
    return "v1";
});

const LIFECYCLE_LABELS = Object.freeze({
    approved: "Approved",
    in_review: "In review",
    changes_requested: "Changes requested",
    rejected: "Rejected",
    uploaded: "Uploaded",
    digitizing: "Digitizing",
    awaiting_approval: "Awaiting approval",
});
const lifecycleState = computed(() => props.lifecycle || "approved");
const lifecycleLabel = computed(
    () => LIFECYCLE_LABELS[lifecycleState.value] ?? "Approved",
);

// Readability -- real ink-vs-garment WCAG contrast (#2791).
//
// Was hardcoded ok:true with the ratio picked from the variant kind
// (15.8 / 12.1 / 4.5), so it could not fail -- not even for the
// dark-ink-on-dark-garment case the badge exists to catch. The
// computation is shared with RoutingStrip via utils/artworkReadability
// so the two surfaces cannot drift apart.
//
// Ink source, in the precedence the old TODO called for: the resolved
// Artwork's ink_color_hex wins, falling back to the variant's legacy
// column. Deliberately NOT `inkHex` -- that computed substitutes
// "#ffffff" so the ink swatch always renders, and feeding a placeholder
// into the contrast math would produce a confident verdict about a
// color nobody declared. Both columns are nullable; when neither is set
// readabilityFor returns null and the badge renders nothing.
//
// Garment side is the routed color subset, not the whole product, and
// the verdict reports the WORST color in it.
//
// The old TODO also wanted the threshold config-driven (3.0 / 4.5 / 7).
// Deliberately still one constant (READABILITY_MIN_RATIO) -- see the
// note in utils/artworkReadability.
const readability = computed(() => {
    const artworkInk = props.artwork?.ink_color_hex;
    const declaredInk =
        typeof artworkInk === "string" && artworkInk.trim()
            ? artworkInk
            : props.variant?.ink_color_hex ?? null;

    return readabilityFor({
        inkHex: declaredInk,
        garmentColors: routedColorsFor(props.variant, props.productColors),
    });
});

function onReplace() {
    emit("replace", {
        variantId: props.variantId,
        placementId: props.placementId,
    });
}

function onSendForApproval() {
    emit("send-for-approval", {
        artifactId: props.variant?.artwork_file_id ?? null,
        variantId: props.variantId,
        placementId: props.placementId,
        // Prefer the resolved Artwork filename when present (Phase 1-A
        // data path); fall back to the legacy artwork_filename. The
        // parent uses this for the modal's "decoration name" header.
        decorationName: formatArtworkName(
            props.artwork?.filename || props.variant?.artwork_filename,
        ),
    });
}
</script>

<style scoped>
.variant-card {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 12px;
    border: 1px solid var(--tf-border, #e0e0e0);
    border-radius: 8px;
    background: var(--tf-bg-card, #ffffff);
}
.variant-card.is-previewing {
    border-color: var(--tf-brand-primary, #119ae9);
    box-shadow: 0 0 0 2px var(--tf-bg-selected, #e6f3fb);
}

/*
  Kind-aware background tint (locked 2026-06-21).
  - bg-light: subtle cool light gray-blue. Foreground stays dark.
  - bg-dark:  dark gray-blue (mirrors design .vcard.dark #1a2332).
    Foreground colors lighten so meta text stays legible.
  - bg-neutral: no override (keeps current white card surface).
*/
.variant-card.variant-card-bg-light {
    background: #f4f7fa;
    border-color: #dfe6ed;
}
.variant-card.variant-card-bg-dark {
    background: #1a2332;
    border-color: #28384e;
    color: #e6edf5;
}
.variant-card.variant-card-bg-dark .variant-card-title,
.variant-card.variant-card-bg-dark .variant-card-art-name {
    color: #ffffff;
}
.variant-card.variant-card-bg-dark .variant-card-routes-sub,
.variant-card.variant-card-bg-dark .variant-card-filename {
    color: #9eb3ce;
}
.variant-card.variant-card-bg-dark .variant-card-color-count {
    background: rgba(255, 255, 255, 0.12);
    color: #cfd8e3;
}
.variant-card.variant-card-bg-dark .variant-card-ink {
    color: #cfd8e3;
}
.variant-card.variant-card-bg-dark .variant-card-version {
    background: rgba(255, 255, 255, 0.1);
    color: #cfd8e3;
}
.variant-card.variant-card-bg-dark .variant-card-thumb {
    background: #0e1620;
    border-color: #28384e;
}
.variant-card.variant-card-bg-dark .variant-card-thumb-empty {
    color: #9eb3ce;
}
.variant-card.variant-card-bg-dark .variant-card-readability[data-state="ok"] {
    background: rgba(46, 125, 50, 0.22);
    color: #a6dbab;
}
.variant-card.variant-card-bg-dark .variant-card-readability[data-state="warn"] {
    background: rgba(217, 119, 6, 0.22);
    color: #f5c577;
}
.variant-card.variant-card-bg-dark .variant-card-replace,
.variant-card.variant-card-bg-dark .variant-card-split,
.variant-card.variant-card-bg-dark .variant-card-merge,
.variant-card.variant-card-bg-dark .variant-card-send {
    background: rgba(255, 255, 255, 0.06);
    border-color: rgba(255, 255, 255, 0.18);
    color: #e6edf5;
}
.variant-card.variant-card-bg-dark .variant-card-replace:hover,
.variant-card.variant-card-bg-dark .variant-card-split:hover,
.variant-card.variant-card-bg-dark .variant-card-merge:hover,
.variant-card.variant-card-bg-dark .variant-card-send:hover {
    background: rgba(255, 255, 255, 0.12);
}

.variant-card-header {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}
.variant-card-title {
    flex: 1 1 auto;
    font-size: 13.5px;
    font-weight: 600;
    color: var(--tf-text-primary, #133752);
}
/* Slice 1: click-to-edit title. The title-wrap keeps the title + role
   chip together and takes the flex slot the plain title used to hold. */
.variant-card-title-wrap {
    flex: 1 1 auto;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
}
.variant-card-title-btn {
    /* Look like plain text, behave like a button (renders the hover
       affordance so the click-to-edit is discoverable). */
    flex: 0 1 auto;
    background: none;
    border: none;
    padding: 1px 4px;
    margin: -1px -4px;
    border-radius: 4px;
    text-align: left;
    cursor: text;
    color: inherit;
    font: inherit;
    font-size: 13.5px;
    font-weight: 600;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.variant-card-title-btn:hover {
    background: var(--tf-bg-hover, #f3f4f6);
}
.variant-card.variant-card-bg-dark .variant-card-title-btn {
    color: #ffffff;
}
.variant-card.variant-card-bg-dark .variant-card-title-btn:hover {
    background: rgba(255, 255, 255, 0.1);
}
/* Slice 1: routing-role chip beside a custom name. */
.variant-card-role-chip {
    flex: 0 0 auto;
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    font-weight: 700;
    color: var(--tf-text-muted, #6b7280);
    background: var(--tf-bg-subtle, #f5f8fa);
    border-radius: 50px;
    padding: 1px 7px;
}
.variant-card.variant-card-bg-dark .variant-card-role-chip {
    background: rgba(255, 255, 255, 0.12);
    color: #cfd8e3;
}
/* Slice 1: inline name editor. Keep it compact so it fits the header
   row without pushing the other header chips to a new line. */
.variant-card-name-input {
    flex: 1 1 auto;
    min-width: 0;
    max-width: 260px;
}
.variant-card-now-previewing {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    color: var(--tf-brand-primary, #119ae9);
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
}
.variant-card-now-dot {
    display: inline-block;
    width: 7px;
    height: 7px;
    background: var(--tf-brand-primary, #119ae9);
    border-radius: 50%;
}
.variant-card-color-count {
    font-size: 11px;
    color: var(--tf-text-muted, #6b7280);
    background: var(--tf-bg-subtle, #f5f8fa);
    border-radius: 50px;
    padding: 1px 8px;
    font-weight: 600;
}
/* Slice 3: the count chip as a button on Custom cards. Looks like the
   static chip but carries a hover affordance + a tiny edit glyph. */
.variant-card-color-count-btn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    border: 1px solid transparent;
    cursor: pointer;
    font: inherit;
    font-size: 11px;
    font-weight: 600;
}
.variant-card-color-count-btn:hover {
    background: var(--tf-bg-hover, #eef2f6);
    border-color: var(--tf-border, #e0e0e0);
    color: var(--tf-brand-primary, #119ae9);
}
.variant-card-color-count-icon {
    font-size: 12px !important;
}
.variant-card.variant-card-bg-dark .variant-card-color-count-btn:hover {
    background: rgba(255, 255, 255, 0.18);
    color: #ffffff;
}

/* PR-L3 — kind-aware routing subline. */
.variant-card-routes-sub {
    margin: -2px 0 0;
    font-size: 11.5px;
    color: var(--tf-text-muted, #6b7280);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.variant-card-body {
    display: flex;
    align-items: flex-start;
    gap: 12px;
}
.variant-card-thumb {
    flex: 0 0 64px;
    width: 64px;
    height: 64px;
    background: var(--tf-bg-thumb, #f8fafc);
    border: 1px solid var(--tf-border, #e0e0e0);
    border-radius: 4px;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
}
.variant-card-thumb img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
}
/* The colorway render is on its way: the default-color picture underneath is
   dimmed so it does not read as the chosen threads, with a light sweep on
   top. Both reset the moment the colorway picture (or the fallback) lands. */
.variant-card-thumb {
    position: relative;
}
.variant-card-thumb.is-loading-colorway img {
    opacity: 0.45;
}
.variant-card-thumb-loading {
    position: absolute;
    inset: 0;
    background: linear-gradient(
        100deg,
        transparent 30%,
        rgba(255, 255, 255, 0.6) 50%,
        transparent 70%
    );
    background-size: 200% 100%;
    animation: variant-card-thumb-sweep 1.2s linear infinite;
    pointer-events: none;
}
@keyframes variant-card-thumb-sweep {
    from {
        background-position: 100% 0;
    }
    to {
        background-position: -100% 0;
    }
}
@media (prefers-reduced-motion: reduce) {
    .variant-card-thumb-loading {
        animation: none;
    }
}
.variant-card-sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
}
.variant-card-thumb-empty {
    font-size: 11px;
    color: var(--tf-text-muted, #6b7280);
    text-align: center;
}
.variant-card-meta {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    gap: 3px;
    min-width: 0;
}
.variant-card-art-name {
    font-size: 13px;
    font-weight: 600;
    color: var(--tf-text-primary, #133752);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
/* The name as a link to the decoration in the library: same type, reads as
   a link on hover/focus, and stays inside the card's text column. */
.variant-card-empty-shade {
    display: flex;
    gap: 8px;
    align-items: flex-start;
    margin: 8px 0;
    padding: 10px 12px;
    border: 2px solid #e65100;
    border-radius: 6px;
    background: #fff3e0;
    color: #6d2a00;
    font-size: 13px;
    line-height: 1.4;
}
.variant-card-empty-shade .material-icons {
    color: #e65100;
    font-size: 20px;
}
.variant-card-art-link {
    display: block;
    max-width: 100%;
    padding: 0;
    border: 0;
    background: none;
    text-align: left;
    cursor: pointer;
}
.variant-card-art-link:hover,
.variant-card-art-link:focus-visible {
    text-decoration: underline;
}
.variant-card-filename {
    font-size: 11px;
    color: var(--tf-fg-4);
    font-family: var(--tf-font-mono, ui-monospace, monospace);
    /* Clamp long filenames so they can't widen the meta column and
       squeeze the layout (Jake 2026-07-10 -- matches .variant-card-art-name). */
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.variant-card-meta-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 2px;
    flex-wrap: wrap;
}
.variant-card-ink {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 11.5px;
    color: var(--tf-text-primary, #133752);
}
.variant-card-ink-dot {
    display: inline-block;
    width: 11px;
    height: 11px;
    border-radius: 50%;
    border: 1px solid rgba(0, 0, 0, 0.18);
    flex-shrink: 0;
}
.variant-card-version {
    font-size: 11px;
    background: var(--tf-bg-subtle, #f5f8fa);
    color: var(--tf-text-muted, #5a6a82);
    border-radius: 4px;
    padding: 1px 6px;
    font-weight: 600;
    font-family: var(--tf-font-mono, ui-monospace, monospace);
}
.variant-card-chip {
    font-size: 10.5px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--tf-text-muted, #6b7280);
    font-weight: 600;
}
.variant-card-chip[data-state="approved"] {
    color: var(--tf-success, #059669);
}
.variant-card-chip[data-state="changes_requested"],
.variant-card-chip[data-state="rejected"] {
    color: var(--tf-danger, #dc2626);
}
.variant-card-chip[data-state="uploaded"],
.variant-card-chip[data-state="digitizing"],
.variant-card-chip[data-state="awaiting_approval"],
.variant-card-chip[data-state="in_review"] {
    color: var(--tf-warning, #d97706);
}
.variant-card-readability {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 11.5px;
    border-radius: 50px;
    padding: 1px 8px;
}
.variant-card-readability[data-state="ok"] {
    background: var(--tf-bg-success-subtle, #ecfdf5);
    color: var(--tf-success, #059669);
}
.variant-card-readability[data-state="warn"] {
    background: var(--tf-bg-warning-subtle, #fef3c7);
    color: var(--tf-warning, #d97706);
}
.variant-card-mi {
    font-size: 13px !important;
}
/*
  Full-width action row (Jake 2026-07-10). Sits BELOW the thumb+meta body
  as a sibling, spanning the card width. Buttons share the width equally
  (flex:1 1 0) with a gap so the long "Send for approval" label can't push
  past the card edge or collide with Replace in the narrow right rail.
*/
/* Colorway binding -- sits between the meta body and the action row. */
.variant-card-colorway {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 8px;
}
.variant-card-colorway-trigger {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 8px;
    font-size: 12px;
    color: var(--tf-text, #111827);
    background: var(--tf-bg-subtle, #f8fafc);
    border: 1px solid var(--tf-border, #e0e0e0);
    border-radius: 4px;
    cursor: pointer;
}
.variant-card-colorway-trigger:hover {
    background: var(--tf-bg-hover, #f1f5f9);
}
.variant-card-colorway-trigger .material-icons {
    font-size: 14px;
}
.variant-card-colorway-menu {
    min-width: 200px;
    padding: 4px 0;
    background: var(--tf-bg-surface, #ffffff);
    border: 1px solid var(--tf-border, #e0e0e0);
    border-radius: 4px;
    box-shadow: 0 4px 12px rgb(0 0 0 / 12%);
}
.variant-card-colorway-option {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 6px 12px;
    font-size: 13px;
    text-align: left;
    background: none;
    border: 0;
    cursor: pointer;
}
.variant-card-colorway-option:hover {
    background: var(--tf-bg-hover, #f1f5f9);
}
.variant-card-colorway-option.is-current {
    font-weight: 600;
}
.variant-card-colorway-option-label {
    flex: 1 1 auto;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
/* A hint, styled as a hint. Deliberately quiet -- it must not read as the
   only permitted choice, because it is not (see colorwayOptions). */
.variant-card-colorway-suggested {
    flex: 0 0 auto;
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--tf-text-muted, #6b7280);
}
.variant-card-colorway-check {
    font-size: 14px;
    color: var(--tf-accent, #2563eb);
}
/* Create-new. Separated by a rule so it reads as an action below the list
   rather than a fourth colorway. */
.variant-card-colorway-create {
    display: flex;
    align-items: center;
    gap: 6px;
    width: 100%;
    padding: 6px 10px;
    border-top: 1px solid var(--tf-border-2, #e2e8f0);
    background: none;
    text-align: left;
    font-size: 12px;
    color: var(--tf-fg-2, #475569);
    cursor: pointer;
}

.variant-card-colorway-create:hover {
    background: var(--tf-bg-hover, #f1f5f9);
}

.variant-card-colorway-create .material-icons {
    font-size: 14px;
}

.variant-card-colorway-summary {
    display: inline-flex;
    align-items: center;
    gap: 4px;
}
.variant-card-colorway-swatch {
    width: 12px;
    height: 12px;
    border: 1px solid var(--tf-border, #e0e0e0);
    border-radius: 2px;
}
.variant-card-colorway-count {
    font-size: 11px;
    color: var(--tf-text-muted, #6b7280);
}
/* #687: full-width line under the trigger, muted like the count beside it. */
.variant-card-colorway-unbound-prompt {
    flex-basis: 100%;
    margin: 0;
    font-size: 11px;
    line-height: 1.4;
    color: var(--tf-text-muted, #6b7280);
}
/* The row WRAPS rather than truncating (Jake, 2026-09-02).

   He photographed this card reading `Replace`, `Use for a...`, `Send re...`.
   The labels were never abbreviated -- they are all present in the template
   above; they were being ellipsed by CSS, which is the worst of both worlds:
   the button no longer says what it does AND nothing signals that text is
   missing beyond three dots.

   The arithmetic says one row cannot hold three: the right rail is a fixed
   340px (.product-editor-right), less 32px rail padding, 2px card border and
   24px card padding, leaving 282px; less two 8px gaps that is ~88px per
   button, and 24px of that is the button's own horizontal padding. ~64px of
   text room, where "Send for approval" needs roughly 118px at 13px. No amount
   of tuning makes three fit on one line at this width.

   So the row is allowed to wrap. `flex-wrap: wrap` plus a `flex-basis` wide
   enough for the longest label means the buttons sit one-per-line (or two-up
   when a shorter pair fits) instead of being shaved. Nothing overflows, which
   was the original 2026-07-10 goal -- that fix moved the buttons OUT of a
   squeezed side-column into this full-width row and solved overflow by
   letting them shrink; shrinking is what has now hit its floor.

   `min-width: 0` and the ellipsis rule are deliberately GONE. They are what
   permitted a button to render narrower than its own text, and keeping them
   as a "safety net" would just reintroduce silent truncation at the next
   width. A label that cannot fit should push the layout, not quietly lose
   characters. */
.variant-card-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 8px;
}
/* Split / merge share the Replace/Send treatment: they sit in the same
   full-width actions row and are peers of it, not a louder or quieter
   affordance. */
.variant-card-replace,
.variant-card-split,
.variant-card-merge,
.variant-card-send {
    /* Grow to share a line, never shrink below the label. The 128px basis
       clears the longest label ("Send for approval", ~118px at 13px) plus
       its padding, so a button that cannot share a line takes its own. */
    flex: 1 1 128px;
    padding: 6px 12px;
    background: none;
    border: 1px solid var(--tf-border, #e0e0e0);
    border-radius: 4px;
    color: var(--tf-text-primary, #133752);
    font-size: 13px;
    cursor: pointer;
    text-align: center;
    white-space: nowrap;
}
.variant-card-replace:hover,
.variant-card-split:hover,
.variant-card-merge:hover,
.variant-card-send:hover {
    background: var(--tf-bg-hover, #f3f4f6);
}

/* Slice 2: kind-menu kebab trigger + dropdown. The trigger mirrors the
   delete-trigger treatment (transparent icon button); the dropdown reuses
   the VariantMenu surface look for consistency across the editor. */
.variant-card-kind-trigger {
    flex: 0 0 auto;
    background: none;
    border: none;
    color: var(--tf-text-muted, #6b7280);
    cursor: pointer;
    padding: 2px 4px;
    border-radius: 4px;
    line-height: 0;
}
.variant-card-kind-trigger:hover {
    color: var(--tf-brand-primary, #119ae9);
    background: var(--tf-bg-hover, #f3f4f6);
}
.variant-card-kind-trigger .material-icons {
    font-size: 18px !important;
}
.variant-card.variant-card-bg-dark .variant-card-kind-trigger {
    color: #cfd8e3;
}
.variant-card.variant-card-bg-dark .variant-card-kind-trigger:hover {
    background: rgba(255, 255, 255, 0.12);
    color: #ffffff;
}
.variant-card-kind-menu {
    background: var(--tf-bg-surface, #ffffff);
    border: 1px solid var(--tf-border, #e0e0e0);
    border-radius: 6px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
    padding: 4px;
    min-width: 200px;
    display: flex;
    flex-direction: column;
    gap: 2px;
}
.variant-card-kind-option {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    width: 100%;
    padding: 8px 10px;
    background: none;
    border: none;
    border-radius: 4px;
    font-size: 13px;
    color: var(--tf-text-primary, #133752);
    cursor: pointer;
    text-align: left;
}
.variant-card-kind-option:hover:not(:disabled) {
    background: var(--tf-bg-hover, #f3f4f6);
}
.variant-card-kind-option:disabled {
    color: var(--tf-fg-5, #949494);
    cursor: not-allowed;
}
.variant-card-kind-option.is-current {
    font-weight: 600;
}
.variant-card-kind-option-check {
    font-size: 16px !important;
    color: var(--tf-brand-primary, #119ae9);
}
.variant-card-kind-option-hint {
    font-size: 10.5px;
    color: var(--tf-fg-4);
    font-style: italic;
    white-space: nowrap;
}

/* Trash icon in the variant card header. Mirrors the design handoff's
   vc-del button (transparent, color-shifts to danger on hover). */
.variant-card-delete-trigger {
    flex: 0 0 auto;
    background: none;
    border: none;
    color: var(--tf-text-muted, #6b7280);
    cursor: pointer;
    padding: 2px 4px;
    border-radius: 4px;
    line-height: 0;
}
.variant-card-delete-trigger:hover {
    color: var(--tf-color-danger, #b91c1c);
    background: var(--tf-bg-danger-subtle, #fef2f2);
}
.variant-card-delete-trigger .material-icons {
    font-size: 18px !important;
}

/* Inline delete-confirm overlay. Replaces the card body per the
   2026-06-13 handoff variantConfirmCard pattern. */
.variant-card-delete-confirm {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 4px 2px;
}
.variant-card-delete-confirm-header {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13.5px;
    font-weight: 600;
    color: var(--tf-color-danger, #b91c1c);
}
.variant-card-delete-confirm-header .material-icons {
    font-size: 18px !important;
}
.variant-card-delete-confirm-body {
    margin: 0;
    font-size: 12px;
    color: var(--tf-text-primary, #133752);
    line-height: 1.45;
}
.variant-card-delete-confirm-actions {
    display: flex;
    gap: 8px;
    justify-content: flex-end;
}
.variant-card-delete-cancel,
.variant-card-delete-confirm-button {
    padding: 6px 12px;
    border-radius: 4px;
    font-size: 13px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 4px;
}
.variant-card-delete-cancel {
    background: none;
    border: 1px solid var(--tf-border, #e0e0e0);
    color: var(--tf-text-primary, #133752);
}
.variant-card-delete-cancel:hover {
    background: var(--tf-bg-hover, #f3f4f6);
}
.variant-card-delete-confirm-button {
    background: var(--tf-color-danger, #b91c1c);
    border: 1px solid var(--tf-color-danger, #b91c1c);
    color: #ffffff;
}
.variant-card-delete-confirm-button:hover {
    background: var(--tf-color-danger-hover, #991b1b);
}
.variant-card-delete-confirm-button .material-icons {
    font-size: 14px !important;
}
</style>
